const SCROLL_INTO_VIEW_MESSAGE_TYPE = 'maverick:scroll-into-view';
const RECORD_CHANGED_MESSAGE_TYPE = 'maverick:record-changed';
const WRITE_METHODS = [ 'post', 'put', 'patch', 'delete' ];

/**
 * Resolve the postMessage target origin for the Visualforce host outside Salesforce's Canvas
 * proxy. `REACT_APP_SF_EMBED_TARGET_ORIGIN` is the authoritative pin and production must set it.
 * The browser referrer identifies the immediate Canvas proxy, not its Visualforce parent, so an
 * unconfigured local/spike build must fall back to `'*'`. Production fails closed so a missing or
 * malformed environment value can never broadcast session-adjacent messages to an arbitrary host.
 */
const resolveSfEmbedTargetOrigin = (): string => {
  const configured = process.env.REACT_APP_SF_EMBED_TARGET_ORIGIN;
  if (configured) {
    try {
      // Normalise to a bare origin. `postMessage` throws SyntaxError on a targetOrigin it can't
      // parse as a URL (e.g. a host copied without its scheme), which would break the
      // jetcard-created handoff; a parseable value with a trailing slash or path is accepted by
      // the browser but normalised here so the pinned value is exactly what gets compared.
      return new URL(configured).origin;
    } catch {
      // Misconfigured value — local/test builds can still use the wildcard fallback below.
    }
  }
  return process.env.NODE_ENV === 'production' ? '' : '*';
};

/**
 * Salesforce inserts its own Canvas proxy between Maverick and the Visualforce host:
 * Maverick -> Canvas proxy -> Visualforce. Resize and completion messages belong to the
 * Visualforce host, so they must cross two parent-frame boundaries.
 */
export const postToSalesforceVisualforceHost = (message: object): void => {
  if (window.parent === window) {
    return;
  }
  const targetOrigin = resolveSfEmbedTargetOrigin();
  if (!targetOrigin) {
    return;
  }
  window.parent.parent.postMessage(message, targetOrigin);
};

/**
 * Scrolls an element into view, including inside the Salesforce embed on iOS. The embed's frames
 * grow to the form's full height, so the Salesforce page is what has to scroll. Desktop Chrome lets
 * scrollIntoView scroll those cross-origin ancestors; WebKit, which every iOS app uses, does not.
 * The element's position also goes to the Visualforce host, which relays it to the Lightning
 * component that scrolls its own page.
 */
export const scrollIntoViewInHost = (
  element: Element,
  options: { block: ScrollLogicalPosition; behavior?: ScrollBehavior }
): void => {
  element.scrollIntoView?.(options);
  const rect = element.getBoundingClientRect();
  postToSalesforceVisualforceHost({
    type: SCROLL_INTO_VIEW_MESSAGE_TYPE,
    top: Math.round(rect.top + window.scrollY),
    height: Math.round(rect.height),
    block: options.block,
    behavior: options.behavior ?? 'auto',
  });
};

/**
 * Tells the Salesforce record page that Maverick may have changed the Opportunity, so it can
 * refresh fields such as Trip Information that Maverick writes back after a save. Some Maverick
 * reads are POSTs too; the page only rereads the record, so a spare notice costs one request.
 */
export const notifySalesforceOfWrite = (method: string | undefined): void => {
  if (method && WRITE_METHODS.includes(method.toLowerCase())) {
    postToSalesforceVisualforceHost({ type: RECORD_CHANGED_MESSAGE_TYPE });
  }
};
